import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import Breadcrumb from '../../components/Breadcrumb';
import { useEffect, useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function ClosedCellSprayFoamPage() {
  const [activeTab, setActiveTab] = useState('benefits');
  const statsReveal = useScrollReveal({ threshold: 0.3 });
  const savingsReveal = useScrollReveal({ threshold: 0.3 });

  useEffect(() => {
    // Page title
    document.title = 'Closed Cell Foam Yuma AZ | Premium Spray Foam Insulation';

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Premium closed cell spray foam insulation in Yuma, AZ. R-7 per inch, moisture barrier, structural strength. Residential & commercial. Call (928) 427-8140.'
      );
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/closed-cell-spray-foam-yuma');

    // Geo-location tags
    const metaTags = [
      { name: 'geo.region', content: 'US-AZ' },
      { name: 'geo.placename', content: 'Yuma' },
      { name: 'geo.position', content: '32.6927;-114.6277' },
      { name: 'ICBM', content: '32.6927, -114.6277' },
    ];
    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Closed Cell Foam Yuma AZ | Premium Spray Foam Insulation' },
      {
        property: 'og:description',
        content: 'Premium closed cell spray foam insulation in Yuma, AZ. R-7 per inch, moisture barrier, structural strength.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin + '/closed-cell-spray-foam-yuma' },
      {
        property: 'og:image',
        content:
          'https://public.readdy.ai/ai/img_res/009ba33e65ead2ec6a380483fce5f9fa.jpg'
      }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Closed Cell Foam Yuma AZ | Premium Spray Foam Insulation' },
      {
        name: 'twitter:description',
        content: 'Premium closed cell spray foam insulation. R-7 per inch, moisture barrier, structural strength.'
      },
      {
        name: 'twitter:image',
        content:
          'https://readdy.ai/api/search-image?query=Professional%20closed%20cell%20spray%20foam%20insulation%20application%20showing%20dense%20rigid%20foam%20texture%20with%20excellent%20coverage%20and%20precision%20installation%20highlighting%20premium%20quality%20and%20superior%20thermal%20protection&width=1200&height=630&seq=twitter-closed-cell-001&orientation=landscape'
      }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Last-modified tag
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Closed Cell Spray Foam',
          item: window.location.origin + '/closed-cell-spray-foam-yuma'
        }
      ]
    };

    let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb-closed-cell"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-schema', 'breadcrumb-closed-cell');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    // Product schema
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Closed Cell Spray Foam Insulation',
      description: 'Premium closed cell spray foam insulation with R-7 per inch, moisture barrier, and structural strength for residential and commercial applications.',
      brand: {
        '@type': 'Brand',
        name: 'Yuma Spray Foam Pros'
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'PriceSpecification',
          minPrice: '1.50',
          maxPrice: '3.00',
          priceCurrency: 'USD',
          unitText: 'per board foot'
        }
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '89'
      }
    };

    let productScript = document.querySelector('script[data-schema="product-closed-cell"]');
    if (!productScript) {
      productScript = document.createElement('script');
      productScript.setAttribute('type', 'application/ld+json');
      productScript.setAttribute('data-schema', 'product-closed-cell');
      document.head.appendChild(productScript);
    }
    productScript.textContent = JSON.stringify(productSchema);

    // LocalBusiness schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Yuma Spray Foam Pros',
      telephone: '(928) 427-8140',
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 32.6927,
        longitude: -114.6277
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '127'
      }
    };

    let businessScript = document.querySelector('script[data-schema="business-closed-cell"]');
    if (!businessScript) {
      businessScript = document.createElement('script');
      businessScript.setAttribute('type', 'application/ld+json');
      businessScript.setAttribute('data-schema', 'business-closed-cell');
      document.head.appendChild(businessScript);
    }
    businessScript.textContent = JSON.stringify(localBusinessSchema);
  }, []);

  const benefits = [
    {
      icon: 'ri-shield-check-line',
      title: 'Maximum R-Value',
      description: 'Delivers R-6 to R-7 per inch, providing the highest thermal resistance available for maximum energy efficiency.'
    },
    {
      icon: 'ri-drop-line',
      title: 'Moisture Barrier',
      description: 'Impermeable to water and vapor, protecting your structure from moisture infiltration, mold, and mildew growth.'
    },
    {
      icon: 'ri-building-line',
      title: 'Structural Strength',
      description: 'Adds rigidity and racking strength to walls and roofs, enhancing overall building structural integrity.'
    },
    {
      icon: 'ri-windy-line',
      title: 'Complete Air Seal',
      description: 'Creates an airtight barrier that eliminates drafts, air leakage, and energy loss through gaps and cracks.'
    },
    {
      icon: 'ri-time-line',
      title: 'Long-Term Performance',
      description: 'Maintains its R-value and structural properties for decades without settling, sagging, or deteriorating.'
    },
    {
      icon: 'ri-bug-line',
      title: 'Pest Resistant',
      description: 'Dense structure deters insects and rodents, providing an additional layer of protection for your property.'
    }
  ];

  const applications = [
    {
      title: 'Exterior Walls',
      description: 'Optimal for exterior walls requiring maximum thermal protection and moisture barriers.',
      image: 'https://readdy.ai/api/search-image?query=exterior%20wall%20construction%20with%20closed%20cell%20spray%20foam%20insulation%20between%20studs%2C%20dense%20white%20foam%20application%2C%20professional%20installation%20showing%20complete%20coverage%20and%20moisture%20protection&width=800&height=500&seq=closed-app-walls-001&orientation=landscape'
    },
    {
      title: 'Roof Decks',
      description: 'Superior protection for roof assemblies, preventing heat gain and moisture infiltration.',
      image: 'https://readdy.ai/api/search-image?query=commercial%20roof%20deck%20with%20closed%20cell%20spray%20foam%20insulation%2C%20professional%20white%20foam%20application%20on%20metal%20roof%20structure%2C%20industrial%20construction%20detail%20photography&width=800&height=500&seq=closed-app-roof-002&orientation=landscape'
    },
    {
      title: 'Crawl Spaces',
      description: 'Creates impermeable barrier in crawl spaces, preventing ground moisture and improving air quality.',
      image: 'https://readdy.ai/api/search-image?query=home%20crawl%20space%20with%20closed%20cell%20spray%20foam%20on%20foundation%20walls%20and%20floor%20joists%2C%20complete%20moisture%20barrier%20protection%2C%20professional%20sealed%20space%20installation&width=800&height=500&seq=closed-app-crawl-003&orientation=landscape'
    },
    {
      title: 'Metal Buildings',
      description: 'Prevents condensation and provides superior thermal performance for metal structures.',
      image: 'https://readdy.ai/api/search-image?query=metal%20building%20interior%20with%20closed%20cell%20spray%20foam%20on%20steel%20walls%20and%20ceiling%2C%20industrial%20warehouse%20space%20showing%20professional%20foam%20application%20preventing%20condensation&width=800&height=500&seq=closed-app-metal-004&orientation=landscape'
    }
  ];

  const faqs = [
    {
      question: 'What makes closed-cell foam premium?',
      answer: 'Closed-cell foam offers the highest R-value per inch (R-6 to R-7), acts as a complete moisture barrier, adds structural strength, and provides superior long-term performance. Its dense structure makes it the most effective option for demanding applications.'
    },
    {
      question: 'Where should I use closed-cell foam?',
      answer: 'Closed-cell is ideal for exterior walls, roof decks, crawl spaces, basements, and any area requiring moisture protection or maximum thermal performance. It\'s the best choice for Arizona\'s extreme climate conditions.'
    },
    {
      question: 'Is it worth the extra cost?',
      answer: 'For exterior applications and areas requiring moisture barriers, absolutely. The superior R-value means you need less thickness to achieve the same performance, and the moisture protection prevents costly damage over time.'
    },
    {
      question: 'Does it really add structural strength?',
      answer: 'Yes! Closed-cell foam can add significant racking strength to walls and roofs. Studies show it can increase wall strength by up to 300%, making your structure more resistant to wind and seismic forces.'
    },
    {
      question: 'How long does it last?',
      answer: 'Closed-cell foam is extremely durable and can last the lifetime of your building when properly installed. It doesn\'t settle, sag, or lose R-value over time like traditional materials.'
    },
    {
      question: 'Can it be used in commercial buildings?',
      answer: 'Absolutely! Closed-cell foam is excellent for commercial applications including warehouses, retail spaces, offices, and industrial facilities. It meets commercial building codes and provides superior energy efficiency.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Closed Cell Foam' },
        ]}
      />

      {/* Hero */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            PREMIUM PERFORMANCE
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Closed Cell Foam Yuma AZ</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Premium closed‑cell spray foam offering the <strong>highest R‑value</strong>, complete <strong>moisture barrier</strong>, and structural reinforcement for demanding applications in Arizona's extreme desert climate. Our <a href="/insulation-services-arizona" className="text-blue-900 hover:text-blue-700 font-semibold underline">professional insulation services</a> deliver maximum energy efficiency.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a
              href="tel:9284278140"
              className="inline-block bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (928) 427‑8140
            </a>
            <a
              href="/insulation-services-arizona"
              className="inline-block bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div 
            ref={statsReveal.ref}
            className={`grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-1000 ${
              statsReveal.isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">R-6-7</div>
              <div className="text-sm text-gray-600">Per Inch R-Value</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">2.0 lb</div>
              <div className="text-sm text-gray-600">Density Per Cubic Ft</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-emerald-600 mb-2">100%</div>
              <div className="text-sm text-gray-600">Moisture Barrier</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">24hrs</div>
              <div className="text-sm text-gray-600">Cure Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Tabs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Premium Protection Details
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover why closed-cell foam is the superior choice for maximum performance and protection.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setActiveTab('benefits')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'benefits'
                  ? 'bg-blue-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-star-line mr-2"></i>
              Benefits
            </button>
            <button
              onClick={() => setActiveTab('technical')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'technical'
                  ? 'bg-blue-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Technical Details
            </button>
          </div>

          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <>
              {/* Mobile: Horizontal Scroll */}
              <div className="md:hidden relative">
                <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
                  <div className="flex gap-6" style={{ width: 'max-content' }}>
                    {benefits.map((benefit, index) => (
                      <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow" style={{ width: '300px', flexShrink: 0 }}>
                        <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                          <i className={`${benefit.icon} text-2xl text-blue-900`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center text-sm text-gray-500 mt-2">
                  Swipe to see more →
                </div>
              </div>

              {/* Desktop: Grid */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                      <i className={`${benefit.icon} text-2xl text-blue-900`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Technical Details Tab */}
          {activeTab === 'technical' && (
            <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Performance Specifications</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">R-Value per Inch:</span>
                      <span className="text-gray-900">R-6 to R-7</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Density:</span>
                      <span className="text-gray-900">1.8-2.0 lb/ft³</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Expansion Rate:</span>
                      <span className="text-gray-900">30:1 to 35:1</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Cure Time:</span>
                      <span className="text-gray-900">24 hours</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Water Absorption:</span>
                      <span className="text-gray-900">&lt;2% by volume</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Vapor Permeability:</span>
                      <span className="text-gray-900">Impermeable</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Compressive Strength:</span>
                      <span className="text-gray-900">25-40 psi</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Characteristics</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Rigid Structure:</strong>
                        <p className="text-gray-600 mt-1">Dense, firm texture provides structural reinforcement and dimensional stability.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Moisture Barrier:</strong>
                        <p className="text-gray-600 mt-1">Impermeable to water and vapor, preventing moisture infiltration and damage.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Air Sealing:</strong>
                        <p className="text-gray-600 mt-1">Creates complete airtight seal, eliminating drafts and air leakage.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Structural Enhancement:</strong>
                        <p className="text-gray-600 mt-1">Adds racking strength and rigidity to walls, roofs, and building envelope.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Long-Term Stability:</strong>
                        <p className="text-gray-600 mt-1">Maintains performance for decades without settling or deteriorating.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="mt-12 pt-12 border-t border-gray-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Energy Savings Potential</h3>
                <div 
                  ref={savingsReveal.ref}
                  className={`grid md:grid-cols-3 gap-6 transition-all duration-1000 ${
                    savingsReveal.isVisible 
                      ? 'opacity-100 translate-y-0' 
                      : 'opacity-0 translate-y-8'
                  }`}
                >
                  <div className="text-center p-6 bg-blue-50 rounded-xl transform hover:scale-105 transition-all duration-300">
                    <div className="text-3xl font-bold text-blue-900 mb-2">40-60%</div>
                    <div className="text-sm text-gray-700">Cooling Cost Reduction</div>
                  </div>
                  <div className="text-center p-6 bg-blue-50 rounded-xl transform hover:scale-105 transition-all duration-300">
                    <div className="text-3xl font-bold text-blue-900 mb-2">30-50%</div>
                    <div className="text-sm text-gray-700">Heating Cost Reduction</div>
                  </div>
                  <div className="text-center p-6 bg-emerald-50 rounded-xl transform hover:scale-105 transition-all duration-300">
                    <div className="text-3xl font-bold text-emerald-600 mb-2">3-5 yrs</div>
                    <div className="text-sm text-gray-700">Typical ROI Period</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Closed Cell vs Open Cell
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding the differences helps you choose the right solution for your project.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 p-8 rounded-xl border-2 border-blue-200">
              <div className="text-center mb-6">
                <div className="inline-block bg-blue-900 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  YOU ARE HERE
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Closed Cell Foam</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>R-6 to R-7 per inch</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Premium performance</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-0.5"></i>
                  <span>Complete moisture barrier</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-0.5"></i>
                  <span>Vapor impermeable (sealed)</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Adds structural strength</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Best for exterior walls & roofs</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Open Cell Foam</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>R-3.5 to R-4 per inch</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>More affordable option</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Superior sound dampening</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Vapor permeable (breathable)</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Lightweight application</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Best for interior walls & attics</span>
                </li>
              </ul>
              <div className="mt-6">
                <a
                  href="/open-cell-spray-foam"
                  className="inline-block w-full text-center bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Learn About Open Cell
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Closed Cell */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Closed Cell?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The premium choice for maximum protection and performance in demanding applications.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-temp-hot-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Desert Climate</h3>
              <p className="text-gray-600">
                Extreme heat in <strong>Arizona</strong> requires maximum R-value and complete air sealing for optimal comfort and <strong>energy efficiency</strong>. Perfect for both residential homes and <a href="/commercial-insulation-yuma" className="text-blue-900 hover:text-blue-700 font-semibold underline">commercial buildings</a>.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-water-flash-line text-3xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Moisture Protection</h3>
              <p className="text-gray-600">
                Monsoon season and irrigation systems make <strong>moisture barriers</strong> essential for long-term building protection in the region. Our <a href="/about-yuma-insulation" className="text-blue-900 hover:text-blue-700 font-semibold underline">experienced team</a> ensures proper installation for maximum protection.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-md text-center hover:shadow-lg transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Energy Savings</h3>
              <p className="text-gray-600">
                Higher R-value means greater <strong>cooling cost reduction</strong>, crucial for managing high summer electricity bills. Check our <a href="/insulation-faq" className="text-blue-900 hover:text-blue-700 font-semibold underline">FAQ page</a> for common questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explore Related Services
            </h2>
            <p className="text-lg text-gray-600">
              Discover our complete range of insulation solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <a 
              href="/open-cell-spray-foam" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-contrast-drop-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                Open Cell Foam
              </h4>
              <p className="text-gray-600 text-sm">
                Affordable soundproofing
              </p>
            </a>
            <a 
              href="/yuma-projects" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-image-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                View Projects
              </h4>
              <p className="text-gray-600 text-sm">
                See completed work
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready for Maximum Protection?
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Get a free consultation and estimate for your project. Our <strong>experts</strong> will show you how closed-cell foam can transform your building's performance.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:9284278140"
              className="inline-block bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (928) 427‑8140
            </a>
            <a
              href="/services"
              className="inline-block bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer border-2 border-white/20"
            >
              View All Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}